import { useEffect, useState } from "react";

// Input numerico que SE PUEDE VACIAR mientras editas. Arregla el bug que
// reporto el dueño: con `parseInt(x) || fallback` en el onChange de un input
// controlado, borrar el campo lo forzaba a 0 (no se podia dejar vacio) y
// reescribir dejaba el 0 adelante ("010").
//
// Solucion: el campo mantiene un BUFFER string interno. El usuario puede
// vaciarlo o tipear libremente; recien en blur (o Enter) se normaliza:
// clamp a [min,max], redondeo a entero si el step es entero, y se emite el
// numero al padre. Si al salir quedo vacio o invalido, se restituye el
// ultimo valor valido (no se rompe el estado del padre).
export function NumberField({
  value,
  onChange,
  min,
  max,
  step = 1,
  id,
  ariaLabel,
  className,
}: {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  id?: string;
  ariaLabel?: string;
  className?: string;
}) {
  const [draft, setDraft] = useState(String(value));

  // Sincronizar el buffer cuando el valor cambia desde afuera (ej. al mover
  // un slider hermano). No piso lo que el usuario esta tipeando salvo que el
  // valor externo realmente difiera.
  useEffect(() => {
    setDraft((d) => (d.trim() !== "" && Number(d) === value ? d : String(value)));
  }, [value]);

  function commit() {
    const raw = draft.trim();
    if (raw === "" || Number.isNaN(Number(raw))) {
      setDraft(String(value)); // vacio/invalido -> restaurar ultimo valido
      return;
    }
    let n = Number(raw);
    if (min !== undefined) n = Math.max(min, n);
    if (max !== undefined) n = Math.min(max, n);
    if (Number.isInteger(step)) n = Math.round(n);
    onChange(n);
    setDraft(String(n));
  }

  return (
    <input
      type="number"
      inputMode="decimal"
      id={id}
      aria-label={ariaLabel}
      min={min}
      max={max}
      step={step}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
      className={
        className ??
        "w-24 rounded-lg bg-white/10 px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-[#a78bfa]"
      }
    />
  );
}
